// Global fetch interceptor for session management.
//
// Wraps window.fetch to transparently handle expired access tokens:
// 1. Any 401 while authenticated triggers a silent token refresh via /auth/refresh.
// 2. On success the original request is retried once with the new cookie.
// 3. If the refresh itself fails (refresh token expired/revoked), the auth store
//    is reset and the user is redirected to /login with a return_to param.
//
// A module-level refreshPromise ensures concurrent 401s share one refresh call
// instead of firing N parallel refresh requests.
//
// Must be called from main.ts after both Pinia and the router are installed.
import type { Router } from 'vue-router'
import { AuthService, OpenAPI } from '@/api'
import { captureApiRequestId, captureApiTraceparent } from '@/observability/reporter'
import { createTraceparent, parseTraceparent } from '@/observability/traceContext'
import { useAuthStore } from '@/stores/auth'

interface RefreshAttempt {
  generation: number
  promise: Promise<unknown>
}

let refreshAttempt: RefreshAttempt | null = null

interface PreparedRequest {
  createArguments: () => Parameters<typeof fetch>
  traceparent?: string | null
}

function prepareRequest(args: Parameters<typeof fetch>, requestUrl: URL): PreparedRequest {
  const apiOrigin = new URL(OpenAPI.BASE || '/', window.location.origin).origin
  if (requestUrl.origin !== apiOrigin || requestUrl.pathname === '/client-events') {
    return { createArguments: () => args }
  }

  try {
    const [input, init] = args
    const isRequest =
      typeof globalThis.Request === 'function' && input instanceof globalThis.Request
    const inputHeaders =
      typeof input === 'object' && input !== null && 'headers' in input ? input.headers : undefined
    const headers = new globalThis.Headers(init?.headers ?? inputHeaders)
    headers.delete('tracestate')
    const context = parseTraceparent(headers.get('traceparent')) ?? createTraceparent()
    headers.set('traceparent', context.traceparent)

    if (isRequest) {
      const request = new globalThis.Request(input, { ...init, headers })
      return {
        createArguments: () => [request.clone()],
        traceparent: context.traceparent,
      }
    }
    return {
      createArguments: () => [input, { ...init, headers }],
      traceparent: context.traceparent,
    }
  } catch {
    return { createArguments: () => args, traceparent: null }
  }
}

function captureResponseContext(
  response: Pick<globalThis.Response, 'headers'>,
  traceparent: string | null | undefined,
): void {
  captureApiRequestId(response)
  if (traceparent !== undefined) captureApiTraceparent(traceparent)
}

export function setupApiInterceptors(router: Router): void {
  const nativeFetch = window.fetch.bind(window)

  window.fetch = async function (...args: Parameters<typeof fetch>) {
    const requestInput = args[0]
    const requestUrl = new URL(
      typeof requestInput === 'string' || requestInput instanceof URL
        ? String(requestInput)
        : requestInput.url,
      window.location.origin,
    )
    if (requestUrl.pathname === '/client-events') return nativeFetch(...args)
    const prepared = prepareRequest(args, requestUrl)
    const response = await nativeFetch(...prepared.createArguments())
    captureResponseContext(response, prepared.traceparent)

    if (response.status !== 401) return response

    const auth = useAuthStore()
    // Only intercept mid-session 401s. fetchUser() sets status to 'loading'
    // before calling /auth/me, so this guard also suppresses the interceptor
    // during the initial page-load check — fetchUser() handles that refresh
    // path itself. Login failures (status = 'unauthenticated') are also excluded.
    if (auth.status !== 'authenticated') return response

    // Skip the refresh endpoint itself to prevent an infinite loop.
    if (requestUrl.pathname === '/auth/refresh') return response

    const generation = auth.captureAuthGeneration()
    // Concurrent 401s from the same authenticated generation share one refresh.
    // A newer successful login must never inherit or be invalidated by an older
    // generation's refresh result.
    if (!refreshAttempt || refreshAttempt.generation !== generation) {
      const attempt: RefreshAttempt = {
        generation,
        promise: Promise.resolve(),
      }
      attempt.promise = AuthService.refreshEndpointAuthRefreshPost().finally(() => {
        if (refreshAttempt === attempt) refreshAttempt = null
      })
      refreshAttempt = attempt
    }
    const attempt = refreshAttempt

    try {
      await attempt.promise
      if (!auth.isAuthGenerationCurrent(generation) || auth.status !== 'authenticated') {
        return response
      }
      const retryResponse = await nativeFetch(...prepared.createArguments())
      captureResponseContext(retryResponse, prepared.traceparent)
      return retryResponse
    } catch {
      if (!auth.isAuthGenerationCurrent(generation) || auth.status !== 'authenticated') {
        return response
      }
      auth.invalidateSession()
      const returnTo = window.location.pathname + window.location.search
      router.push({ name: 'login', query: { return_to: returnTo } })
      return response
    }
  }
}
